import { useCallback, useEffect, useRef, type RefObject } from "react";

/* JOB 3 STEP 5 PAGE 6 — A WINDOW MOUNTED ON DEMAND RETURNS FOCUS TO WHAT OPENED IT. Page 1 mounted the reminder window only
 * once `Remind me…` was pressed (it sat in the page as a closed `<dialog>` with its four fields); a modal that UNMOUNTS
 * returns focus nowhere, so the opener is focused again once it is gone. A page with several openers (a button, a row's
 * title, a menu's item) calls `remember()` as it opens — or `remember(el)` naming the control that stays (a menu's ⋯, when
 * its item unmounts with the menu); when that control is gone too, the page's heading takes focus — never `<body>`. */
export function useOpenerFocus(isOpen: boolean, fallback: RefObject<HTMLElement | null>): (el?: HTMLElement | null) => void {
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen) { wasOpen.current = true; return; }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    const el = opener.current;
    opener.current = null;
    (el !== null && el.isConnected ? el : fallback.current)?.focus();
  }, [isOpen, fallback]);
  return useCallback((el?: HTMLElement | null) => {
    const a = document.activeElement;
    opener.current = el ?? (a instanceof HTMLElement ? a : null);
  }, []);
}
